import { types } from "../types/types";
import axios from 'axios';
import { crearErrorAction, crearTotOkAction,esborrarErrorAction , uiIniciarCarregaAction, uiFinalitzarCarregaAction } from "./ui";

const peticioApi = axios.create({
    headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8', 'Accept': 'application/json' },
    withCredentials: true,
    baseURL: process.env.REACT_APP_DOMINI_API_CIUDAD
});

const toFormBody = (obj) =>
    Object.entries(obj)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v ?? '')}`)
        .join('&');

// ── Cargar municipios ─────────────────────────────────────────────────────────
export const cargarTodosMunicipios = (token) => async (dispatch) => {
    try {
        const { data } = await peticioApi.get('/admin/municipios', {
            headers: { Authorization: token }
        });
        if (data.ok) {
            dispatch({ type: types.adminCarregarMunicipis, payload: data.contingut || [] });
        } else {
            dispatch(crearErrorAction(data.msg || 'Error al cargar municipios'));
        }
    } catch {
        dispatch(crearErrorAction('Error de conexión al cargar municipios'));
    }
};

// ── Cargar usuarios ───────────────────────────────────────────────────────────
export const cargarTodosUsuarios = (token) => async (dispatch) => {
    dispatch(uiIniciarCarregaAction());
    try {
        const { data } = await peticioApi.get('/admin/usuarios', {
            headers: { Authorization: token }
        });
        if (data.ok) {
            dispatch({ type: types.adminCarregarUsuaris, payload: data.contingut || [] });
        } else {
            dispatch(crearErrorAction(data.msg || 'Error al cargar usuarios'));
        }
    } catch {
        dispatch(crearErrorAction('Error de conexión al cargar usuarios'));
    } finally {
        dispatch(uiFinalitzarCarregaAction());
    }
};

// ── Eliminar municipio ────────────────────────────────────────────────────────
// Borra el municipio, sus evaluaciones y los responsables no-admin
export const eliminarMunicipio = (token, IdMunicipio) => async (dispatch) => {
    dispatch(uiIniciarCarregaAction());
    try {
        const { data } = await peticioApi.post(
            '/admin/municipio/eliminar',
            toFormBody({ IdMunicipio }),
            { headers: { Authorization: token } }
        );
        if (data.ok) {
            dispatch(crearTotOkAction('Municipio eliminado correctamente'));
            dispatch(cargarTodosMunicipios(token));
        } else {
            dispatch(crearErrorAction(data.msg || 'Error al eliminar municipio'));
        }
    } catch {
        dispatch(crearErrorAction('Error de conexión'));
    } finally {
        dispatch(uiFinalitzarCarregaAction());
    }
};

// ── Eliminar usuario ──────────────────────────────────────────────────────────
// Solo borra el responsable, el municipio no se toca
export const eliminarUsuario = (token, IdResponsable) => async (dispatch) => {
    dispatch(uiIniciarCarregaAction());
    try {
        const { data } = await peticioApi.post(
            '/admin/usuario/eliminar',
            toFormBody({ IdResponsable }),
            { headers: { Authorization: token } }
        );
        if (data.ok) {
            dispatch(crearTotOkAction('Usuario eliminado correctamente'));
            dispatch(cargarTodosUsuarios(token));
        } else {
            dispatch(crearErrorAction(data.msg || 'Error al eliminar usuario'));
        }
    } catch {
        dispatch(crearErrorAction('Error de conexión'));
    } finally {
        dispatch(uiFinalitzarCarregaAction());
    }
};

// ── Actualizar usuario ────────────────────────────────────────────────────────
// Actualiza datos del responsable y, si viene IdMunicipio distinto, cambia su municipio
export const actualizarUsuarioAdmin = (token, IdResponsable, datosUsuario) => async (dispatch) => {
    dispatch(uiIniciarCarregaAction());
    try {
        const { data } = await peticioApi.post(
            '/admin/usuario/actualizar',
            toFormBody({ IdResponsable, ...datosUsuario }),
            { headers: { Authorization: token } }
        );
        if (data.ok) {
            dispatch(crearTotOkAction('Usuario actualizado correctamente'));
            dispatch(cargarTodosUsuarios(token));
        } else {
            dispatch(crearErrorAction(data.msg || 'Error al actualizar usuario'));
        }
    } catch {
        dispatch(crearErrorAction('Error de conexión'));
    } finally {
        dispatch(uiFinalitzarCarregaAction());
    }
};

// ── Actualizar municipio ──────────────────────────────────────────────────────
// Recibe FormData (puede incluir Foto_Mun como archivo)
export const actualizarMunicipio = (token, formData) => async (dispatch) => {
    dispatch(uiIniciarCarregaAction());
    try {
        const { data } = await peticioApi.post(
            '/admin/municipio/actualizar',
            formData,   // FormData → axios no sobreescribe el Content-Type
            { headers: { Authorization: token } }
        );
        if (data.ok) {
            dispatch(crearTotOkAction('Municipio actualizado correctamente'));
            dispatch(cargarTodosMunicipios(token));
        } else {
            dispatch(crearErrorAction(data.msg || 'Error al actualizar municipio'));
        }
    } catch {
        dispatch(crearErrorAction('Error de conexión'));
    } finally {
        dispatch(uiFinalitzarCarregaAction());
    }
};

// ── Crear municipio ───────────────────────────────────────────────────────────
// Recibe FormData (puede incluir Foto_Mun como archivo). Solo crea el municipio, sin responsable.
// Devuelve { ok, IdMunicipio } para que el componente pueda encadenar la creación del responsable.
export const crearMunicipio = (token, formData) => async (dispatch) => {
    dispatch(uiIniciarCarregaAction());
    try {
        const { data } = await peticioApi.post(
            '/admin/municipio/crear',
            formData,
            { headers: { Authorization: token } }
        );
        if (data.ok) {
            dispatch(crearTotOkAction('Municipio creado correctamente'));
            dispatch(cargarTodosMunicipios(token));
            return { ok: true, IdMunicipio: data.IdMunicipio };
        } else {
            dispatch(crearErrorAction(data.msg || 'Error al crear municipio'));
            return { ok: false };
        }
    } catch {
        dispatch(crearErrorAction('Error de conexión'));
        return { ok: false };
    } finally {
        dispatch(uiFinalitzarCarregaAction());
    }
};

// ── Crear usuario ─────────────────────────────────────────────────────────────
// datos debe incluir: Nombre_Resp, Apellidos_Resp, Email_Resp, Telf_Resp (opcional), IdMunicipio
// El backend envía email al responsable para que establezca su contraseña.
export const crearUsuario = (token, datos) => async (dispatch) => {
    dispatch(uiIniciarCarregaAction());
    try {
        const { data } = await peticioApi.post(
            '/admin/usuario/crear',
            toFormBody(datos),
            { headers: { Authorization: token } }
        );
        if (data.ok) {
            dispatch(crearTotOkAction('Usuario creado. Se ha enviado email de activación.'));
            dispatch(cargarTodosUsuarios(token));
        } else {
            dispatch(crearErrorAction(data.msg || 'Error al crear usuario'));
        }
    } catch {
        dispatch(crearErrorAction('Error de conexión'));
    } finally {
        dispatch(uiFinalitzarCarregaAction());
    }
};

export const recuperarConclusiones = (token, IdEvaluacion, onOk) => {
    return (dispatch) => {
        const requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                'Accept': 'application/json',
                'Authorization': token,
            },
            credentials: 'include',
            mode: 'cors',
        };

        fetch(process.env.REACT_APP_DOMINI_API_CIUDAD + '/conclusiones/' + IdEvaluacion, requestOptions)
            .then(async response => {
                const isJson = response.headers.get('content-type')?.includes('application/json');
                const data = isJson && await response.json();

                if (response.ok && data.ok && data.contingut?.Conclusiones) {
                    if (onOk) onOk(data.contingut.Conclusiones);
                }
            })
            .catch(error => {
                console.error('Error recuperarConclusiones:', error);
            });
    }
}

export const guardarConclusiones = (token, IdEvaluacion, texto, onOk, onError) => {
    return (dispatch) => {

        var dadesFormulari = {
            'IdEvaluacion': IdEvaluacion,
            'Conclusiones': texto,
        };

        var formBody = [];
        for (var property in dadesFormulari) {
            var encodedKey   = encodeURIComponent(property);
            var encodedValue = encodeURIComponent(dadesFormulari[property]);
            formBody.push(encodedKey + "=" + encodedValue);
        }
        formBody = formBody.join("&");

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                'Accept': 'application/json',
                'Authorization': token,
            },
            credentials: 'include',
            body: formBody,
            mode: 'cors',
        };

        fetch(process.env.REACT_APP_DOMINI_API_CIUDAD + '/conclusiones/guardar', requestOptions)
            .then(async response => {
                const isJson = response.headers.get('content-type')?.includes('application/json');
                const data = isJson && await response.json();

                if (!response.ok) {
                    dispatch(crearErrorAction("Error al guardar conclusiones: " + JSON.stringify(data)));
                    if (onError) onError("Error de conexión.");
                } else {
                    if (data.ok) {
                        dispatch(crearTotOkAction("Conclusiones guardades correctament"));
                        dispatch(esborrarErrorAction());
                        if (onOk) onOk(IdEvaluacion, texto);
                    } else {
                        const msg = data.msg_es || "Error al guardar las conclusiones.";
                        dispatch(crearErrorAction(msg));
                        if (onError) onError(msg);
                    }
                }
            })
            .catch(error => {
                console.error('Error guardarConclusiones:', error);
                if (onError) onError("Error de conexión. Inténtalo de nuevo.");
            });
    }
}

export const cargarUsuariosMunicipio = (token, idMunicipio) => {
    return (dispatch) => {
        const requestOptions = {
            method: 'GET',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8', 'Accept': 'application/json', 'Authorization': token },
            credentials: 'include',
            mode: 'cors'
        };
        fetch(`${process.env.REACT_APP_DOMINI_API_CIUDAD}/admin/municipio/${idMunicipio}/usuarios`, requestOptions)
            .then(async response => {
                const isJson = response.headers.get('content-type')?.includes('application/json');
                const data = isJson && await response.json();
                if (response.ok && data.ok) {
                    dispatch({ type: types.adminCarregarUsuarisMunicipi, contingut: data.contingut || [] });
                } else {
                    dispatch(crearErrorAction(data?.msg || 'Error al cargar usuarios'));
                }
            })
            .catch(error => { console.error('There was an error!', error); });
    };
};